import React, { useEffect, useState, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  Box,
  Heading,
  Text,
  VStack,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Alert,
  AlertIcon,
  Input,
  Select,
  HStack,
  Button,
  Flex,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  SimpleGrid,
  Grid,
  GridItem,
  Icon,
  InputGroup,
  InputLeftElement,
  Badge,
  RangeSlider,
  RangeSliderTrack,
  RangeSliderFilledTrack,
  RangeSliderThumb,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useColorModeValue,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Divider,
  Checkbox,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Progress,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react';

import { 
  ChevronUpIcon, 
  ChevronDownIcon, 
} from '@chakra-ui/icons';

import {
  MdFilterList,
  MdRefresh,
  MdSearch,
  MdDownload,
  MdClear,
  MdAttachMoney,
  MdSync,
  MdCloudDownload,
} from 'react-icons/md';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL
  : process.env.REACT_APP_LOCAL_API_URL;

const ITEMS_PER_PAGE_OPTIONS = [5, 10, 20, 50];

const PrecatoriosMG = () => {
  const [precatorios, setPrecatorios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);
  const [isBuscando, setIsBuscando] = useState(false);
  const [buscandoTodas, setBuscandoTodas] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const toast = useToast();

  // Entity selection for buscar
  const [entidades, setEntidades] = useState([]);
  const [selectedEntidade, setSelectedEntidade] = useState('');

  // Search & filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterNatureza, setFilterNatureza] = useState('');
  const [filterEnte, setFilterEnte] = useState('');
  const [filterTipoAcao, setFilterTipoAcao] = useState('');
  const [priceRange, setPriceRange] = useState([0, 0]);
  const [currentPriceRange, setCurrentPriceRange] = useState([0, 0]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isProgressOpen, onOpen: onProgressOpen, onClose: onProgressClose } = useDisclosure();

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  
  // State for tracking selected precatorios
  const [selectedPrecatorios, setSelectedPrecatorios] = useState([]);

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const headingColor = useColorModeValue('gray.800', 'white');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');
  const highlightColor = useColorModeValue('brand.primary', 'brand.secondary');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  // Fetch entities list on mount
  useEffect(() => {
    const fetchEntidades = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/precatorioMG/entidades`);
        const data = await res.json();
        if (data.success) {
          setEntidades(data.entidades);
        }
      } catch (err) {
        console.error('Erro ao buscar entidades:', err);
      }
    };

    fetchEntidades();
  }, []);

  // Fetch precatorios on mount
  useEffect(() => {
    const fetchPrecatorios = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/precatorioMG`);
        const data = await res.json();
        setPrecatorios(data);
        
        // Initialize price range based on data
        if (data.length > 0) {
          const values = data.map(p => p.valor_atualizado || 0);
          const minValue = Math.max(0, Math.floor(Math.min(...values)));
          const maxValue = Math.ceil(Math.max(...values));
          setPriceRange([minValue, maxValue]);
          setCurrentPriceRange([minValue, maxValue]);
        }
      } catch (err) {
        console.error(err);
        setErro(true);
      } finally {
        setLoading(false);
      }
    };

    fetchPrecatorios();
  }, []);

  // Handle buscar for single entity
  const handleBuscar = async () => {
    if (!selectedEntidade) {
      toast({
        title: 'Selecione uma entidade',
        description: 'Por favor, selecione uma entidade devedora para buscar',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsBuscando(true);
    setProgressMessage('Buscando precatórios do TJMG...');
    onProgressOpen();

    try {
      const selectedEntity = entidades.find(e => e.value === selectedEntidade);
      
      const res = await fetch(`${API_BASE_URL}/precatorioMG/buscar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          entidade_label: selectedEntity?.label,
          entidade_value: selectedEntidade 
        }),
      });

      const data = await res.json();

      if (data.success) {
        toast({
          title: 'Busca concluída',
          description: data.message,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // Refresh the data
        await refreshData();
      } else {
        toast({
          title: 'Erro na busca',
          description: data.message || 'Não foi possível buscar os dados',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Erro ao buscar dados:', err);
      toast({
        title: 'Erro na busca',
        description: 'Ocorreu um erro ao tentar buscar os dados',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsBuscando(false);
      onProgressClose();
    }
  };

  // Handle buscar todas entidades
  const handleBuscarTodas = async () => {
    setBuscandoTodas(true);
    setProgressMessage('Buscando precatórios de todas as entidades...');
    onProgressOpen();

    try {
      const res = await fetch(`${API_BASE_URL}/precatorioMG/buscar-todas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await res.json();

      if (data.success) {
        toast({
          title: 'Busca concluída',
          description: `Processadas ${data.statistics?.entidadesProcessadas || 0} entidades. Inseridos: ${data.statistics?.totalInserted || 0}, Atualizados: ${data.statistics?.totalUpdated || 0}`,
          status: 'success',
          duration: 8000,
          isClosable: true,
        });

        // Refresh the data
        await refreshData();
      } else {
        toast({
          title: 'Busca parcialmente concluída',
          description: data.message || 'Algumas entidades não puderam ser processadas',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Erro ao buscar dados:', err);
      toast({
        title: 'Erro na busca',
        description: 'Ocorreu um erro ao tentar buscar os dados',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setBuscandoTodas(false);
      onProgressClose();
    }
  };

  // Refresh data
  const refreshData = async () => {
    try {
      const refreshRes = await fetch(`${API_BASE_URL}/precatorioMG`);
      const refreshData = await refreshRes.json();
      setPrecatorios(refreshData);

      // Update price range
      if (refreshData.length > 0) {
        const values = refreshData.map(p => p.valor_atualizado || 0);
        const minValue = Math.max(0, Math.floor(Math.min(...values)));
        const maxValue = Math.ceil(Math.max(...values));
        setPriceRange([minValue, maxValue]);
        setCurrentPriceRange([minValue, maxValue]);
      }
    } catch (err) {
      console.error('Erro ao atualizar dados:', err);
    }
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const SortIcon = ({ isActive, direction }) => {
    return direction === 'asc' ? (
      <ChevronUpIcon ml={2} opacity={isActive ? 1 : 0.3} />
    ) : (
      <ChevronDownIcon ml={2} opacity={isActive ? 1 : 0.3} />
    );
  };

  // Extract unique filters from data
  const naturezas = useMemo(() => [...new Set(precatorios.map(p => p.natureza).filter(Boolean))], [precatorios]);
  const entes = useMemo(() => [...new Set(precatorios.map(p => p.ente || p.entidade_devedora).filter(Boolean))], [precatorios]);
  const tiposAcao = useMemo(() => [...new Set(precatorios.map(p => p.tipo_acao).filter(Boolean))], [precatorios]);

  const filteredData = useMemo(() => {
    let result = [...precatorios];
  
    // Search + Filters
    result = result.filter(p =>
      (p.numero_precatorio || '').toLowerCase().includes(searchTerm.toLowerCase()) &&
      (!filterNatureza || p.natureza === filterNatureza) &&
      (!filterEnte || (p.ente === filterEnte || p.entidade_devedora === filterEnte)) &&
      (!filterTipoAcao || p.tipo_acao === filterTipoAcao) &&
      (p.valor_atualizado || 0) >= currentPriceRange[0] &&
      (p.valor_atualizado || 0) <= currentPriceRange[1]
    );
  
    // Sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
    
        // Handle 'recebimento' as a date
        if (sortConfig.key === 'recebimento' || sortConfig.key === 'data_liquidacao') {
          const aDate = new Date((aVal || '').split('/').reverse().join('-'));
          const bDate = new Date((bVal || '').split('/').reverse().join('-'));
    
          return sortConfig.direction === 'asc'
            ? aDate - bDate
            : bDate - aDate;
        }
    
        // Handle numeric values
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
        }
    
        // Fallback: string comparison
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
    
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [precatorios, searchTerm, filterNatureza, filterEnte, filterTipoAcao, currentPriceRange, sortConfig]);
  
  // Pagination calculations
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(start, start + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage]);

  // Handlers
  const handlePageChange = (direction) => {
    setCurrentPage(prev => {
      if (direction === 'next' && prev < totalPages) return prev + 1;
      if (direction === 'prev' && prev > 1) return prev - 1;
      return prev;
    });
  };
  
  // Handler for saving selected precatorios
  const handleSaveSelection = async () => {
    if (selectedPrecatorios.length === 0) {
      toast({
        title: 'Nenhum precatório selecionado',
        description: 'Selecione pelo menos um precatório para salvar',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    try {
      toast({
        title: 'Salvando seleção',
        description: 'Aguarde enquanto salvamos os precatórios selecionados...',
        status: 'loading',
        duration: 3000,
        isClosable: true,
        id: 'save-selection',
      });
      
      const response = await fetch(`${API_BASE_URL}/precatorioMG/selecionar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ precatorios: selectedPrecatorios }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: 'Sucesso!',
          description: `${data.count} precatórios foram marcados como selecionados.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
          id: 'save-selection',
        });
        setSelectedPrecatorios([]);
      } else {
        throw new Error(data.message || 'Erro ao salvar seleção');
      }
    } catch (error) {
      console.error('Erro ao salvar seleção:', error);
      toast({
        title: 'Erro',
        description: `Erro ao salvar seleção: ${error.message}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
        id: 'save-selection',
      });
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterNatureza('');
    setFilterEnte('');
    setFilterTipoAcao('');
    setCurrentPriceRange([priceRange[0], priceRange[1]]);
    setCurrentPage(1);
  };

  const formatCurrency = (value) => {
    return `R$ ${(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  };

  // Handle manual price input changes with validation
  const handlePriceRangeChange = (values) => {
    const minValue = Math.max(0, values[0]);
    const maxValue = Math.max(minValue, values[1]);
    setCurrentPriceRange([minValue, maxValue]);
  };

  // Handle export functionality
  const handleExport = (format = 'csv') => {
    try {
      const dataToExport = filteredData.map(item => ({
        'Ordem': item.ordem,
        'Número Precatório': item.numero_precatorio,
        'Natureza': item.natureza,
        'Orçamento': item.orcamento,
        'Recebimento': item.recebimento,
        'Prioridade': item.prioridade || '-',
        'Valor Atualizado': formatCurrency(item.valor_atualizado),
        'Ente': item.ente || item.entidade_devedora,
        'Tipo Ação': item.tipo_acao || '-',
        'Origem': item.origem || '-',
        'Processo Execução': item.processo_execucao || '-',
        'Status': item.status || 'Não Contatou'
      }));

      const fileName = `precatorios_MG_${new Date().toISOString().split('T')[0]}`;

      if (format === 'xlsx') {
        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Precatórios MG');
        
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `${fileName}.xlsx`);
      } else {
        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const csv = XLSX.utils.sheet_to_csv(ws, { FS: ',', RS: '\n' });
        
        const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, `${fileName}.csv`);
      }

      toast({
        title: 'Exportação concluída',
        description: `${filteredData.length} registros exportados com sucesso`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Erro ao exportar:', error);
      toast({
        title: 'Erro na exportação',
        description: 'Não foi possível exportar os dados',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Calculate total value
  const totalValue = useMemo(() => {
    return filteredData.reduce((acc, item) => acc + (item.valor_atualizado || 0), 0);
  }, [filteredData]);

  return (
    <Box p={{ base: 4, md: 8 }}>
      {/* Progress Modal */}
      <Modal isOpen={isProgressOpen} onClose={() => {}} isCentered closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Buscando Precatórios</ModalHeader>
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <Spinner size="xl" color="blue.500" thickness="4px" />
              <Text textAlign="center">{progressMessage}</Text>
              <Text fontSize="sm" color="gray.500" textAlign="center">
                Esta operação pode levar alguns minutos...
              </Text>
              <Progress size="xs" isIndeterminate w="100%" colorScheme="blue" />
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Header Section */}
      <Flex 
        direction={{ base: 'column', md: 'row' }} 
        justify="space-between" 
        align={{ base: 'flex-start', md: 'center' }} 
        mb={8}
      >
        <Box mb={{ base: 4, md: 0 }}>
          <Heading 
            fontSize={{ base: '2xl', md: '3xl' }} 
            fontWeight="bold"
            color={headingColor}
            mb={2}
          >
            Precatórios – TJ-MG
          </Heading>
          <Text color={textColor}>
            Lista de Precatórios do Tribunal de Justiça de Minas Gerais
          </Text>
        </Box>
        <HStack spacing={3}>
          <Button
            leftIcon={<Icon as={MdRefresh} />}
            variant="outline"
            size="sm"
            onClick={() => window.location.reload()}
          >
            Atualizar Página
          </Button>
          <Menu>
            <MenuButton
              as={Button}
              leftIcon={<Icon as={MdDownload} />}
              rightIcon={<ChevronDownIcon />}
              variant="solid"
              colorScheme="blue"
              size="sm"
            >
              Exportar
            </MenuButton>
            <MenuList>
              <MenuItem onClick={() => handleExport('csv')}>
                Exportar como CSV
              </MenuItem>
              <MenuItem onClick={() => handleExport('xlsx')}>
                Exportar como Excel
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>

      {/* Buscar Card */}
      <Card 
        bg={cardBg}
        borderRadius="lg"
        boxShadow="sm"
        overflow="hidden"
        border="1px"
        borderColor={borderColor}
        mb={6}
      >
        <CardHeader pb={2}>
          <Heading size="md" color={headingColor}>
            Buscar Precatórios do TJMG
          </Heading>
        </CardHeader>
        <CardBody>
          <Grid 
            templateColumns={{ base: "1fr", md: "2fr 1fr 1fr" }}
            gap={4}
            alignItems="flex-end"
          >
            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm">Entidade Devedora</FormLabel>
                <Select 
                  placeholder="Selecione uma entidade"
                  value={selectedEntidade}
                  onChange={(e) => setSelectedEntidade(e.target.value)}
                >
                  {entidades.map((entidade) => (
                    <option key={entidade.value} value={entidade.value}>
                      {entidade.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </GridItem>
            <GridItem>
              <Button
                leftIcon={<Icon as={MdSync} />}
                variant="solid"
                colorScheme="green"
                w="full"
                onClick={handleBuscar}
                isLoading={isBuscando}
                loadingText="Buscando..."
              >
                Buscar Entidade
              </Button>
            </GridItem>
            <GridItem>
              <Button
                leftIcon={<Icon as={MdCloudDownload} />}
                variant="solid"
                colorScheme="purple"
                w="full"
                onClick={handleBuscarTodas}
                isLoading={buscandoTodas}
                loadingText="Buscando..."
              >
                Buscar Todas
              </Button>
            </GridItem>
          </Grid>
          <Text fontSize="xs" color="gray.500" mt={3}>
            Selecione uma entidade específica ou busque todas as 47 entidades estaduais do TJMG.
          </Text>
        </CardBody>
      </Card>

      {loading ? (
        <Card
          bg={cardBg}
          borderRadius="lg"
          boxShadow="sm"
          overflow="hidden"
          border="1px"
          borderColor={borderColor}
        >
          <CardBody py={12} textAlign="center">
            <VStack spacing={4}>
              <Spinner size="xl" color={highlightColor} thickness="4px" />
              <Text>Carregando dados de precatórios...</Text>
            </VStack>
          </CardBody>
        </Card>
      ) : erro ? (
        <Alert status="error" borderRadius="lg" mb={6}>
          <AlertIcon />
          Erro ao buscar os dados de precatórios. Tente novamente mais tarde.
        </Alert>
      ) : (
        <>
          {/* Stats Cards */}
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={6}>
            <Card
              bg={cardBg}
              borderRadius="lg"
              boxShadow="sm"
              overflow="hidden"
              border="1px"
              borderColor={borderColor}
              transition="all 0.3s"
              _hover={{
                boxShadow: 'md',
                transform: 'translateY(-2px)'
              }}
            >
              <CardBody p={5}>
                <Stat>
                  <StatLabel color={textColor}>Total de Precatórios</StatLabel>
                  <StatNumber>{totalItems}</StatNumber>
                  <StatHelpText>{totalItems === 1 ? 'Registro encontrado' : 'Registros encontrados'}</StatHelpText>
                </Stat>
              </CardBody>
            </Card>
            
            <Card
              bg={cardBg}
              borderRadius="lg"
              boxShadow="sm"
              overflow="hidden"
              border="1px"
              borderColor={borderColor}
              transition="all 0.3s"
              _hover={{
                boxShadow: 'md',
                transform: 'translateY(-2px)'
              }}
            >
              <CardBody p={5}>
                <Stat>
                  <StatLabel color={textColor}>Valor Total</StatLabel>
                  <StatNumber>{formatCurrency(totalValue)}</StatNumber>
                  <StatHelpText>Soma dos valores atualizados</StatHelpText>
                </Stat>
              </CardBody>
            </Card>
            
            <Card
              bg={cardBg}
              borderRadius="lg"
              boxShadow="sm"
              overflow="hidden"
              border="1px"
              borderColor={borderColor}
              transition="all 0.3s"
              _hover={{
                boxShadow: 'md',
                transform: 'translateY(-2px)'
              }}
            >
              <CardBody p={5}>
                <Stat>
                  <StatLabel color={textColor}>Valor Médio</StatLabel>
                  <StatNumber>
                    {totalItems > 0 
                      ? formatCurrency(totalValue / totalItems) 
                      : 'R$ 0,00'}
                  </StatNumber>
                  <StatHelpText>Por precatório</StatHelpText>
                </Stat>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* Filters Card */}
          <Card 
            bg={cardBg}
            borderRadius="lg"
            boxShadow="sm"
            overflow="hidden"
            border="1px"
            borderColor={borderColor}
            mb={6}
          >
            <CardBody py={4}>
              <Grid 
                templateColumns={{ base: "1fr", md: "1fr 1fr 1fr auto auto" }}
                gap={4}
                alignItems="center"
              >
                <GridItem>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Icon as={MdSearch} color="gray.400" />
                    </InputLeftElement>
                    <Input 
                      placeholder="Buscar por nº precatório" 
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </InputGroup>
                </GridItem>
                
                <GridItem>
                  <Select 
                    placeholder="Filtrar por natureza"
                    value={filterNatureza}
                    onChange={(e) => {
                      setFilterNatureza(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    {naturezas.map((natureza) => (
                      <option key={natureza} value={natureza}>
                        {natureza}
                      </option>
                    ))}
                  </Select>
                </GridItem>
                
                <GridItem>
                  <Select 
                    placeholder="Filtrar por entidade"
                    value={filterEnte}
                    onChange={(e) => {
                      setFilterEnte(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    {entes.map((ente) => (
                      <option key={ente} value={ente}>
                        {ente}
                      </option>
                    ))}
                  </Select>
                </GridItem>
                
                <GridItem>
                  <Button 
                    leftIcon={<Icon as={MdFilterList} />}
                    onClick={onOpen}
                    size="md"
                    variant="outline"
                    w="full"
                  >
                    Mais Filtros
                  </Button>
                </GridItem>
                
                <GridItem>
                  <HStack justifyContent="flex-end">
                    <Select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      size="md"
                      w="150px"
                    >
                      {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option} por página
                        </option>
                      ))}
                    </Select>
                    
                    {(searchTerm || filterNatureza || filterEnte || filterTipoAcao ||
                      currentPriceRange[0] !== priceRange[0] || 
                      currentPriceRange[1] !== priceRange[1]) && (
                      <Button 
                        leftIcon={<Icon as={MdClear} />}
                        onClick={clearFilters}
                        size="md"
                        variant="ghost"
                        colorScheme="red"
                      >
                        Limpar
                      </Button>
                    )}
                  </HStack>
                </GridItem>
              </Grid>

              {/* Advanced Filters Drawer */}
              <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
                <DrawerOverlay />
                <DrawerContent>
                  <DrawerCloseButton />
                  <DrawerHeader borderBottomWidth="1px">
                    Filtros Avançados
                  </DrawerHeader>

                  <DrawerBody>
                    <VStack spacing={6} align="stretch" pt={4}>
                      <FormControl>
                        <FormLabel fontWeight="medium">Tipo de Ação</FormLabel>
                        <Select 
                          placeholder="Todos os tipos"
                          value={filterTipoAcao}
                          onChange={(e) => {
                            setFilterTipoAcao(e.target.value);
                            setCurrentPage(1);
                          }}
                        >
                          {tiposAcao.map((tipo) => (
                            <option key={tipo} value={tipo}>
                              {tipo}
                            </option>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl>
                        <FormLabel fontWeight="medium">
                          Faixa de Valor (R$)
                        </FormLabel>
                        
                        <Flex justifyContent="space-between" alignItems="center" mb={4}>
                          <Input 
                            type="text" 
                            value={`R$ ${currentPriceRange[0].toLocaleString('pt-BR')}`}
                            onChange={(e) => {
                              const value = Number(e.target.value.replace(/[^0-9]/g, ''));
                              if (!isNaN(value)) {
                                setCurrentPriceRange([value, currentPriceRange[1]]);
                              }
                            }}
                            w="140px"
                          />
                          
                          <Input 
                            type="text" 
                            value={`R$ ${currentPriceRange[1].toLocaleString('pt-BR')}`}
                            onChange={(e) => {
                              const value = Number(e.target.value.replace(/[^0-9]/g, ''));
                              if (!isNaN(value)) {
                                setCurrentPriceRange([currentPriceRange[0], value]);
                              }
                            }}
                            w="140px"
                          />
                        </Flex>
                        
                        <RangeSlider
                          min={priceRange[0]}
                          max={priceRange[1]}
                          step={(priceRange[1] - priceRange[0]) / 100 || 1}
                          value={currentPriceRange}
                          onChange={handlePriceRangeChange}
                          colorScheme="blue"
                        >
                          <RangeSliderTrack>
                            <RangeSliderFilledTrack />
                          </RangeSliderTrack>
                          <RangeSliderThumb index={0} />
                          <RangeSliderThumb index={1} />
                        </RangeSlider>
                      </FormControl>

                      <Divider />

                      <Box>
                        <Text fontWeight="medium" mb={2}>Filtros Aplicados:</Text>
                        <VStack align="start" spacing={2}>
                          {searchTerm && (
                            <Badge colorScheme="blue" px={2} py={1} borderRadius="full">
                              Busca: {searchTerm}
                            </Badge>
                          )}
                          {filterNatureza && (
                            <Badge colorScheme="green" px={2} py={1} borderRadius="full">
                              Natureza: {filterNatureza}
                            </Badge>
                          )}
                          {filterEnte && (
                            <Badge colorScheme="purple" px={2} py={1} borderRadius="full">
                              Entidade: {filterEnte}
                            </Badge>
                          )}
                          {filterTipoAcao && (
                            <Badge colorScheme="teal" px={2} py={1} borderRadius="full">
                              Tipo Ação: {filterTipoAcao}
                            </Badge>
                          )}
                          {(currentPriceRange[0] !== priceRange[0] || currentPriceRange[1] !== priceRange[1]) && (
                            <Badge colorScheme="orange" px={2} py={1} borderRadius="full">
                              Valor: {formatCurrency(currentPriceRange[0])} - {formatCurrency(currentPriceRange[1])}
                            </Badge>
                          )}
                        </VStack>
                      </Box>
                    </VStack>
                  </DrawerBody>

                  <DrawerFooter borderTopWidth="1px">
                    <Button variant="outline" mr={3} onClick={clearFilters}>
                      Limpar Filtros
                    </Button>
                    <Button colorScheme="blue" onClick={onClose}>
                      Aplicar
                    </Button>
                  </DrawerFooter>
                </DrawerContent>
              </Drawer>
            </CardBody>
          </Card>

          {/* Results Card */}
          <Card 
            bg={cardBg}
            borderRadius="lg"
            boxShadow="sm"
            overflow="hidden"
            border="1px"
            borderColor={borderColor}
            mb={6}
          >
            <CardHeader pb={2}>
              <Flex justify="space-between" align="center">
                <Heading size="md" color={headingColor}>
                  Precatórios Encontrados
                </Heading>
                <Badge colorScheme="blue" fontSize="sm" py="1" px="2" borderRadius="md">
                  {totalItems} {totalItems === 1 ? 'resultado' : 'resultados'}
                </Badge>
              </Flex>
            </CardHeader>
            
            <CardBody p={0}>
              <Box overflowX="auto" w="100%">
                <Table variant="simple" size="sm">
                  <Thead bg={tableHeaderBg}>
                    <Tr>
                      <Th onClick={() => handleSort('ordem')} cursor="pointer">
                        <Flex align="center">
                          Ordem
                          <SortIcon
                            isActive={sortConfig.key === 'ordem'}
                            direction={sortConfig.key === 'ordem' ? sortConfig.direction : 'asc'}
                          />
                        </Flex>
                      </Th>

                      <Th onClick={() => handleSort('numero_precatorio')} cursor="pointer">
                        <Flex align="center">
                          Nº Precatório
                          <SortIcon
                            isActive={sortConfig.key === 'numero_precatorio'}
                            direction={sortConfig.key === 'numero_precatorio' ? sortConfig.direction : 'asc'}
                          />
                        </Flex>
                      </Th>

                      <Th onClick={() => handleSort('natureza')} cursor="pointer">
                        <Flex align="center">
                          Natureza
                          <SortIcon
                            isActive={sortConfig.key === 'natureza'}
                            direction={sortConfig.key === 'natureza' ? sortConfig.direction : 'asc'}
                          />
                        </Flex>
                      </Th>

                      <Th onClick={() => handleSort('recebimento')} cursor="pointer">
                        <Flex align="center">
                          Recebimento
                          <SortIcon
                            isActive={sortConfig.key === 'recebimento'}
                            direction={sortConfig.key === 'recebimento' ? sortConfig.direction : 'asc'}
                          />
                        </Flex>
                      </Th>

                      <Th onClick={() => handleSort('valor_atualizado')} cursor="pointer">
                        <Flex align="center">
                          Valor
                          <SortIcon
                            isActive={sortConfig.key === 'valor_atualizado'}
                            direction={sortConfig.key === 'valor_atualizado' ? sortConfig.direction : 'asc'}
                          />
                        </Flex>
                      </Th>

                      <Th onClick={() => handleSort('tipo_acao')} cursor="pointer">
                        <Flex align="center">
                          Tipo Ação
                          <SortIcon
                            isActive={sortConfig.key === 'tipo_acao'}
                            direction={sortConfig.key === 'tipo_acao' ? sortConfig.direction : 'asc'}
                          />
                        </Flex>
                      </Th>

                      <Th onClick={() => handleSort('entidade_devedora')} cursor="pointer">
                        <Flex align="center">
                          Entidade
                          <SortIcon
                            isActive={sortConfig.key === 'entidade_devedora'}
                            direction={sortConfig.key === 'entidade_devedora' ? sortConfig.direction : 'asc'}
                          />
                        </Flex>
                      </Th>

                      <Th textAlign="center">
                        <Checkbox
                          colorScheme="green"
                          isChecked={paginatedData.length > 0 && paginatedData.every(item => selectedPrecatorios.includes(item._id))}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const currentPageIds = paginatedData.map(item => item._id);
                              const newSelection = [...selectedPrecatorios];
                              
                              currentPageIds.forEach(id => {
                                if (!newSelection.includes(id)) {
                                  newSelection.push(id);
                                }
                              });
                              
                              setSelectedPrecatorios(newSelection);
                            } else {
                              const currentPageIds = paginatedData.map(item => item._id);
                              setSelectedPrecatorios(selectedPrecatorios.filter(id => !currentPageIds.includes(id)));
                            }
                          }}
                          size="lg"
                        />
                      </Th>
                    </Tr>
                  </Thead>

                  <Tbody>
                    {paginatedData.length > 0 ? (
                      paginatedData.map((item) => (
                        <Tr 
                          key={item._id || item.numero_precatorio}
                          _hover={{ bg: hoverBg }}
                          transition="background-color 0.2s"
                        >
                          <Td>{item.ordem}</Td>
                          <Td fontWeight="medium" color={highlightColor}>{item.numero_precatorio}</Td>
                          <Td>
                            <Badge colorScheme={item.natureza?.toLowerCase().includes('alimentar') ? 'green' : 'purple'} variant="subtle">
                              {item.natureza}
                            </Badge>
                          </Td>
                          <Td>{item.recebimento}</Td>
                          <Td fontWeight="medium">{formatCurrency(item.valor_atualizado)}</Td>
                          <Td>{item.tipo_acao || '-'}</Td>
                          <Td>
                            <Text fontSize="xs" noOfLines={2}>
                              {item.entidade_devedora || item.ente}
                            </Text>
                          </Td>
                          <Td textAlign="center">
                            <Checkbox 
                              colorScheme="green"
                              isChecked={selectedPrecatorios.includes(item._id)}
                              onChange={() => {
                                if (selectedPrecatorios.includes(item._id)) {
                                  setSelectedPrecatorios(selectedPrecatorios.filter(id => id !== item._id));
                                } else {
                                  setSelectedPrecatorios([...selectedPrecatorios, item._id]);
                                }
                              }}
                              size="lg"
                            />
                          </Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={8} textAlign="center" py={8}>
                          <VStack spacing={3}>
                            <Text color={textColor}>
                              {precatorios.length === 0 
                                ? 'Nenhum precatório cadastrado. Use o botão "Buscar" para importar dados do TJMG.'
                                : 'Nenhum precatório encontrado com os filtros selecionados'}
                            </Text>
                            {precatorios.length > 0 && (
                              <Button 
                                size="sm"
                                variant="outline"
                                leftIcon={<Icon as={MdClear} />}
                                onClick={clearFilters}
                              >
                                Limpar Filtros
                              </Button>
                            )}
                          </VStack>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
            </CardBody>

            {paginatedData.length > 0 && (
              <CardFooter borderTopWidth="1px" borderColor={borderColor} justifyContent="space-between" flexDirection="column">
                <Flex width="100%" justifyContent="space-between" mb={selectedPrecatorios.length > 0 ? 4 : 0}>
                  <Text fontSize="sm" color={textColor}>
                    Página {currentPage} de {totalPages} ({totalItems} registros)
                  </Text>
                  <HStack>
                    <Button 
                      onClick={() => handlePageChange('prev')} 
                      isDisabled={currentPage === 1}
                      size="sm"
                      variant="outline"
                    >
                      Anterior
                    </Button>
                    <Button 
                      onClick={() => handlePageChange('next')} 
                      isDisabled={currentPage === totalPages}
                      size="sm"
                      variant="solid"
                      colorScheme="blue"
                    >
                      Próxima
                    </Button>
                  </HStack>
                </Flex>
                
                {selectedPrecatorios.length > 0 && (
                  <Flex width="100%" justifyContent="center" mt={2}>
                    <Button
                      onClick={handleSaveSelection}
                      colorScheme="green"
                      leftIcon={<Icon as={MdAttachMoney} />}
                      size="md"
                    >
                      Salvar Seleção ({selectedPrecatorios.length} precatórios)
                    </Button>
                  </Flex>
                )}
              </CardFooter>
            )}
          </Card>

          {/* Info Footer */}
          <Flex 
            mt={10} 
            justify="space-between" 
            fontSize="xs" 
            color={textColor}
            borderTop="1px"
            borderColor={borderColor}
            pt={4}
          >
            <Text>Fonte: API TJ-MG | Atualização manual via botão "Buscar"</Text>
            <Text>
              Em caso de dúvidas, entre em contato com o suporte técnico
            </Text>
          </Flex>
        </>
      )}
    </Box>
  );
};

export default PrecatoriosMG;
